const d3 = require('d3-geo');

// Blurs one polygon in place on an already-drawn canvas context.
// The panorama is drawn once by the caller; nothing is re-encoded per polygon.
export const stackBlurImage = (context, radius, blurAlphaChannel, points) => {
    return new Promise((res, rej) => {
        var width = context.canvas.width;
        var height = context.canvas.height;
        var top_x = 0;
        var top_y = 0;

        if (isNaN(radius) || radius < 1) return res();

        var imageData;
        var originalWidth = width;
        var originalHeight = height;
        var originalImageData = null;

        var poseRoll = 0;
        var posePitch = 0;

        if(!clockwiseDirections(points.map(function (coord) {
            return {
                x: coord.yaw,
                y: coord.pitch
            };
        }))) points = points.slice().reverse();

        radius |= 0;

        var geoJSON = {
            "type": "Feature",
            "properties": {"featurecla": "Country", "scalerank": 1},
            "geometry": {
                "type": "Polygon",
                "coordinates": [[]]
            }
        }

        const borderCoordsDeg = getCoordSphereDeg(points);
        const borderCoordsRad = getLineCoords(borderCoordsDeg);
        const borderSphereRad = borderCoordsRad.map(([yaw, pitch]) => {return {x: deg2rad(yaw), y: deg2rad(pitch)}});
        geoJSON.geometry.coordinates[0] = geoJSON.geometry.coordinates[0].concat(borderCoordsRad);

        var {minYawRad,
            minPitchRad,
            maxYawRad,
            maxPitchRad,
        } = getBoundingBox(geoJSON, originalWidth, originalHeight,{poseRoll, posePitch}, points)

        let [minx, maxy] = sphere2equi(minYawRad, minPitchRad, poseRoll, posePitch, originalWidth, originalHeight);
        let [maxx, miny] = sphere2equi(maxYawRad, maxPitchRad, poseRoll, posePitch, originalWidth, originalHeight);

        var isDropSphere = minYawRad > maxYawRad || maxx === width;

        minx = parseInt(minx);
        miny = parseInt(miny);
        maxx = parseInt(maxx);
        maxy = parseInt(maxy);

        if(isDropSphere){
            minx = 0;
            maxx = width;
        }

        try {
            try {
                if(!isDropSphere){
                    width = Math.abs(maxx-minx);
                    height = Math.abs(maxy-miny);
                    top_x = minx;
                    top_y = miny;
                }

                imageData = context.getImageData( top_x, top_y, width, height );
                // The full-size copy is only written to by the worker in the
                // isDropSphere branch; otherwise it only needs the dimensions.
                originalImageData = isDropSphere
                    ? context.getImageData( 0, 0, originalWidth, originalHeight )
                    : {width: originalWidth, height: originalHeight, data: null};
            } catch(e) {
                rej("Can't get context " + e);

                return false;
            }
        } catch(e) {
            rej("unable to access image data: " + e);

            return false;
        }

        // Send plain {data, width, height} objects so the pixel buffers are
        // transferred (zero-copy) instead of structured-cloned.
        const toPlain = (d) => ({data: d.data, width: d.width, height: d.height});
        const sendImageData = toPlain(imageData);
        const sendOriginalImageData = toPlain(originalImageData);
        const transfer = [sendImageData.data.buffer];
        if (sendOriginalImageData.data) transfer.push(sendOriginalImageData.data.buffer);
        imageData = originalImageData = null;

        const worker = new Worker( '/build/worker-blur.js' );
        worker.onmessage = function(e) {
            worker.terminate();
            if(e.data){
                const out = isDropSphere ? e.data.originalImageData : e.data.imageData;

                context.putImageData(
                    new ImageData(out.data, out.width, out.height),
                    isDropSphere ? 0 : top_x,
                    isDropSphere ? 0 : top_y
                );

                res();
            }else{
                rej("Can't finish processing: empty worker response");
            }
        }

        worker.onerror = function(e) {
            worker.terminate();
            rej("Can't finish processing " + (e && e.message ? e.message : e));

            return false;
        }

        worker.postMessage({radius,
            geoJSON,
            borderSphereRad,
            originalImageData: sendOriginalImageData,
            imageData: sendImageData,
            minx,
            miny,
            maxx,
            maxy,
            isDropSphere}, transfer); // Start the worker.
    })
}

function clockwiseDirections(points) {
    let subX = Math.min(0, Math.min.apply(null, points.map((p) => p.x)));
    let subY = Math.min(0, Math.min.apply(null, points.map((p) => p.x)));
    let newPoints = points.map((p) => {
        return {
            x: p.x - subX,
            y: p.y - subY
        }
    });

    let sum = 0;

    for ( let i = 0; i < newPoints.length; i++ ) {
        let c1 = newPoints[i],c2;
        if (i === newPoints.length - 1) {
            c2 = newPoints[0]
        } else {
            c2 = newPoints[i+1];
        }
        sum += (c2.x - c1.x) * (c2.y + c1.y);
    }

    return (sum >= 0);
}

function sphereCoordsCorrection(yaw, pitch, poseRoll, posePitch) {
    let [x, y, z] = spher2Cart(yaw, pitch, poseRoll, posePitch);

    let r = Math.sqrt(x*x + y*y + z*z);

    pitch = Math.asin(Math.max(Math.min(z/r, 1), -1));
    yaw = Math.atan2(x, y);

    return [yaw, pitch];
}

function spher2Cart(yaw, pitch, poseRoll, posePitch) {
    let x = Math.cos(pitch)*Math.sin(yaw),
        y = Math.cos(pitch)*Math.cos(yaw),
        z = Math.sin(pitch);

    if (poseRoll || posePitch) {
        let ox = x * Math.cos(poseRoll) + Math.sin(poseRoll) * ( y * Math.sin(posePitch) + z * Math.cos(posePitch) );
        let oy = y * Math.cos(posePitch) - z * Math.sin(posePitch);
        z = -x * Math.sin(poseRoll) + Math.cos(poseRoll) * ( y * Math.sin(posePitch) + z * Math.cos(posePitch) );
        x = ox; y=oy;
    }

    return [x,y,z];
}

function sphere2equi(yaw, pitch, poseRoll, posePitch, width, height) {
    [yaw, pitch] = sphereCoordsCorrection(yaw, pitch, poseRoll, posePitch)

    let yp = rad2deg(pitch) / -180 + 0.5;
    let xp = (rad2deg(yaw) + 180) / 360;

    return [xp * width, yp * height];
}

function getBoundingBox(geoJSON, originalWidth, originalHeight, {poseRoll, posePitch}){
    const [minDeg, maxDeg] = d3.geoBounds(geoJSON);
    const [minYawDeg, minPitchDeg] = minDeg;
    const [maxYawDeg, maxPitchDeg] = maxDeg;

    var [minX, maxY] = sphere2equi(deg2rad(minYawDeg), deg2rad(minPitchDeg), poseRoll, posePitch, originalWidth, originalHeight);
    var [maxX, minY] = sphere2equi(deg2rad(maxYawDeg), deg2rad(maxPitchDeg), poseRoll, posePitch, originalWidth, originalHeight);

    minX = parseInt(minX);
    minY = parseInt(minY);
    maxX = parseInt(maxX);
    maxY = parseInt(maxY);

    return {minX, minY, maxX, maxY,
        minYawRad: deg2rad(minYawDeg),
        minPitchRad: deg2rad(minPitchDeg),
        maxYawRad: deg2rad(maxYawDeg),
        maxPitchRad: deg2rad(maxPitchDeg),
    }
}

function deg2rad(alpha) {
    return alpha * Math.PI / 180;
}

function rad2deg(alpha) {
    return alpha * 180 / Math.PI;
}

function getCoordSphereDeg(points){
    var gegCoordSphere = [];
    points.forEach((sphere) => {
        var yaw = sphere.yaw;
        var pitch = sphere.pitch;

        gegCoordSphere.push([rad2deg(yaw), rad2deg(pitch)]);
    })

    gegCoordSphere.push(gegCoordSphere[0]);

    return gegCoordSphere;
}

function getLineCoords(borderCoordsDeg){
    const lineCoords = [];
    for(var i = 0; i < borderCoordsDeg.length; i++){
        var londonLonLat = borderCoordsDeg[i];
        var newYorkLonLat = borderCoordsDeg[i+1];
        if(londonLonLat && newYorkLonLat){
            var geoInterpolator = d3.geoInterpolate(londonLonLat, newYorkLonLat);
            for(var steps = 0; steps < 1; steps+=0.1){
                lineCoords.push(geoInterpolator(steps))
            }
        }
    }

    return lineCoords;
}

function addPreview(width, height, imageData){
    if(document.querySelector('#preview')){
        document.body.removeChild(document.querySelector('#preview'))
    }

    var preview  = document.createElement( "canvas" );
    var contextPreview  =preview.getContext("2d");
    preview.width = width;
    preview.height = height;
    preview.id = 'preview';

    preview.style.position = 'absolute';
    preview.style.zIndex = '99999999999';
    preview.style.top = '20px';
    preview.style.right = '20px';

    contextPreview.putImageData( imageData, 0, 0 );
    document.body.appendChild(preview)
}

